import FetchHelper from "./lib/fetchHelper.js";
import DateUtility from "./lib/dateUtility";
import { BPShiftExchangeStatus } from "./lib/bpShiftExchangeStatus";
import DOMPurify from 'dompurify';
import qs from 'qs';
import React from 'react';
import SwapOfferProposeComponent from './SwapOfferProposeComponent';
import ConfirmDialog from './lib/confirmDialog';
import CancelExchangeConfirm from './lib/cancelExchangeConfirm';
import { BYTTEVAGTER_URL, BvCard, BvPage, CopyLinkButton } from './lib/byttevagtPage';
import { SWAP_CHANGED_EVENT, possessive } from './lib/swapShiftLine';
import { overtagQuestion, whenText as whenPhrase } from './lib/acquireShift';
import { ReactRenderer } from './lib/ReactRenderer';
const roleIcon = (role) => React.createElement("i", { className: role === 'Operator' ? 'fas fa-film' : 'fas fa-cash-register', title: role, "aria-hidden": "true" });
const clock = (dt) => DateUtility.parse(dt).toFormat("HH:mm");
const whenText = (dt) => DateUtility.parse(dt).toFormat("dd-MM-yyyy 'kl.' HH:mm");
/**
 * The page of one byttevagt - what a shared link and every "Byt med mig" ends on - in the states the
 * boards "Byttevagt - ..." show: the offer to take it (with the ways it cannot be taken), the volunteer's
 * own byttevagt, and the shift once it is gone. "Byt med mig" is a screen of its own, in
 * SwapOfferProposeComponent.
 *
 * It is the page of `/byttevagter/?slug=`: the list component hands over to it when a slug is in the
 * address, so the landing page and links shared into groups keep ending here.
 */
export default class ShiftExchangeFormComponent extends React.Component {
    constructor(props) {
        super(props);
        /**
         * "Byt med mig" is a step of its own: it has an entry in the browser's history, so Back from it is
         * the card of the byttevagt and not the page the volunteer came from before it. The address says
         * which of the two is up (`swap=1`), and Back and Forward just follow it.
         */
        this.pushedSwap = false;
        this.onPopState = () => {
            var _a;
            const swap = qs.parse(window.location.search, { ignoreQueryPrefix: true }).swap === "1";
            // An entry made by openSwap says so, so Forward onto it still has the card right behind it;
            // an address that was opened with swap=1 (from the list) has not.
            this.pushedSwap = swap && !!((_a = window.history.state) === null || _a === void 0 ? void 0 : _a.bvSwap);
            this.setState({ proposing: swap });
        };
        /** Reads the byttevagt by its slug; resolves with it, or with nothing when it could not be read. */
        this.load = () => {
            if (!this.state.slug)
                return Promise.resolve(null);
            return fetch(`/bpapi/shiftexchange/${this.state.slug}`, { method: 'GET' })
                .then((response) => response.ok
                ? response.json()
                : Promise.reject("Vagtbyttet kunne ikke findes."))
                .then((result) => {
                this.setState({ data: result, isLoaded: true, error: null });
                return result;
            })
                .catch((error) => {
                this.setState({ error: error, isLoaded: true });
                return null;
            });
        };
        /**
         * A volunteer coming back to a byttevagt they have already made an offer on - from Mine
         * byttetilbud, or the shared link a second time - is here to look at or change that offer, so
         * Byt med mig opens straight away with it loaded. Taking the shift outright instead means
         * withdrawing the offer first, which is what Fortryd does.
         *
         * A failure here just leaves the page as it would otherwise be.
         */
        this.openIfAlreadyOffered = () => FetchHelper.get(`/bpapi/shiftswap/mine`)
            .then((mine) => {
            if (mine.items.some((offer) => { var _a; return ((_a = offer.shiftExchange) === null || _a === void 0 ? void 0 : _a.slug) === this.state.slug; })) {
                this.setState({ proposing: true });
            }
        })
            .catch(() => { });
        /**
         * Fortryd is abandoning the whole idea of swapping this shift, so it takes back every offer
         * this volunteer has pending on it - not merely closing the form over live proposals the owner
         * can still accept. The form stays open if the withdrawals fail, so nothing is hidden that is
         * still outstanding.
         */
        this.cancelProposing = () => {
            const form = this.proposeRef.current;
            // Nothing sent yet, so there is nothing to lose: just close the form.
            if (!(form === null || form === void 0 ? void 0 : form.state.offer)) {
                this.closeSwap();
                return;
            }
            // An offer is standing, and Fortryd takes it back - which the volunteer cannot undo from
            // here, and which may well be a mis-tap on a phone. Ask first.
            this.setState({ confirmingCancel: true });
        };
        this.state = {
            data: {
                shift: {}
            },
            submitDisabled: false,
            // "Byt med mig" pressed on the list opens the page with the swap already begun.
            proposing: qs.parse(window.location.search, { ignoreQueryPrefix: true }).swap === "1",
            // Whether the propose form holds a standing offer, as it last reported. Decides whether
            // the cancel button takes that offer back or just closes the form.
            offerStanding: false,
            // The owner's Træk tilbage, while its question is open.
            confirmingWithdraw: false,
            slug: qs.parse(window.location.search, { ignoreQueryPrefix: true }).slug
        };
        // Fortryd has to reach the offers, which the propose form owns.
        this.proposeRef = React.createRef();
    }
    componentDidMount() {
        this.load().then((data) => data && this.openIfAlreadyOffered());
        window.addEventListener("popstate", this.onPopState);
    }
    componentWillUnmount() {
        window.removeEventListener("popstate", this.onPopState);
    }
    openSwap() {
        const query = Object.assign(Object.assign({}, qs.parse(window.location.search, { ignoreQueryPrefix: true })), { swap: "1" });
        window.history.pushState({ bvSwap: true }, "", `${window.location.pathname}?${qs.stringify(query)}`);
        this.pushedSwap = true;
        this.setState({ proposing: true });
    }
    /** Back to the card: one step back when the swap screen was pushed, else the address is tidied. */
    closeSwap() {
        if (this.pushedSwap) {
            this.pushedSwap = false;
            window.history.back();
            return;
        }
        const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
        if (query.swap) {
            delete query.swap;
            window.history.replaceState(null, "", `${window.location.pathname}?${qs.stringify(query)}`);
        }
        this.setState({ proposing: false });
    }
    confirmCancel() {
        this.setState({ cancelling: true });
        this.proposeRef.current.withdrawAll().then((done) => {
            this.setState({ cancelling: false, confirmingCancel: false });
            // Stays open on failure, with the propose form's error showing, so an offer that is
            // still live is not hidden.
            if (done)
                this.closeSwap();
        });
    }
    submit() {
        this.setState({ submitDisabled: true });
        fetch(`/bpapi/shiftexchange/acquire`, FetchHelper.postOptions({ slug: this.state.data.slug, returnUrl: this.state.data.returnUrl }))
            .then((response) => {
            if (response.ok) {
                return response.json();
            }
            else {
                return Promise.reject(`Der opstod en fejl (${response.statusText})`);
            }
        })
            .then((result) => {
            if (result.success) {
                this.setState({ result: result });
                window.dispatchEvent(new CustomEvent(SWAP_CHANGED_EVENT));
            }
            else {
                this.setState({ error: result.errorMessage });
            }
        })
            .catch(error => this.setState({ error: error }));
    }
    /**
     * Who else is on the shift, what is showing and the notes - the same under every card that
     * describes the shift. The volunteer who offered it is left out of the team: it is theirs to give.
     */
    renderShift(data) {
        const team = data.shift.members.filter(x => x.memberId !== data.shiftMember.memberId);
        return (React.createElement(React.Fragment, null,
            React.createElement("p", null,
                "Vagten starter kl. ",
                React.createElement("strong", null, clock(data.shift.dtStart)),
                " og slutter kl ",
                React.createElement("strong", null, clock(data.shift.dtEnd)),
                "."),
            team.length > 0
                ? React.createElement(React.Fragment, null,
                    React.createElement("h3", null, "Vagthold:"),
                    React.createElement("ul", { className: "bi-bv-list" }, team.map((member, idx) => member.name
                        ? React.createElement("li", { key: idx },
                            roleIcon(member.role),
                            member.name)
                        : React.createElement("li", { key: idx, className: "bi-bv-open" },
                            roleIcon(member.role),
                            React.createElement("span", null, "Endnu ikke besat")))))
                : null,
            data.shift.events.length > 0
                ? React.createElement(React.Fragment, null,
                    React.createElement("h3", null, "Begivenheder:"),
                    React.createElement("ul", { className: "bi-bv-list bi-bv-events" }, data.shift.events.map((evt, idx) => React.createElement("li", { key: idx },
                        "(",
                        evt.venue.venueName,
                        ") ",
                        DateUtility.parse(evt.startTime).toFormat("HH:mm"),
                        " \u2022 ",
                        evt.eventContent.title))))
                : null,
            (data.shift.notes && data.shift.notes !== '') ?
                React.createElement(React.Fragment, null,
                    React.createElement("h3", null, "Noter til vagten:"),
                    React.createElement("p", { dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(data.shift.notes) } }))
                : null));
    }
    /** The shift offered, on top of "Byt med mig": whose it is, when, the role and the films. */
    renderProposeHeader(data) {
        var _a;
        const start = DateUtility.parse(data.shift.dtStart);
        const end = DateUtility.parse(data.shift.dtEnd);
        const films = ((_a = data.shift.events) !== null && _a !== void 0 ? _a : []).map((evt) => { var _a; return (_a = evt.eventContent) === null || _a === void 0 ? void 0 : _a.title; }).filter(Boolean);
        return (React.createElement("div", { className: "bi-bp-shift" },
            React.createElement("span", { className: "bi-bp-shift-tile" }, roleIcon(data.shiftMember.role)),
            React.createElement("div", { className: "bi-bp-shift-text" },
                React.createElement("span", { className: "bi-bp-shift-who" },
                    possessive(data.shiftMember.name),
                    " byttevagt"),
                React.createElement("a", { className: "bi-bp-shift-when", href: `/vagtinfo?shiftId=${data.shift.id}` },
                    DateUtility.rowDay(start),
                    " \u00B7 ",
                    DateUtility.rowInterval(start, end)),
                React.createElement("span", { className: "bi-bp-shift-role" }, data.shiftMember.role === 'Operator' ? 'Operatør' : 'Kiosk'),
                films.map((title, idx) => React.createElement("span", { key: idx, className: "bi-bp-shift-film" }, title)))));
    }
    /**
     * After "Byt nu": the byttevagt is closed now, and both shifts have changed hands. Says which,
     * in the same shape as the card "Overtag" ends on.
     */
    renderSwapped() {
        const data = this.state.data;
        const yours = this.state.swapped.yourShift;
        return (React.createElement(BvPage, { title: "Byttevagt" },
            React.createElement(BvCard, { title: "Vagten er byttet" },
                React.createElement("p", null,
                    "Du har overtaget vagten d. ",
                    whenText(data.shift.dtStart),
                    " fra ",
                    data.shiftMember.name,
                    yours ? React.createElement(React.Fragment, null,
                        ", og ",
                        data.shiftMember.name,
                        " har overtaget din vagt d. ",
                        whenText(yours.dtStart)) : null,
                    "."),
                React.createElement("a", { className: "bi-bv-link", href: data.returnUrl || BYTTEVAGTER_URL }, "Tilbage"))));
    }
    /** The owner's own view: the link to share, and the way to take the shift off the exchange. */
    renderOwner(data) {
        return (React.createElement(BvPage, { title: "Byttevagt" },
            React.createElement(BvCard, { title: `Din byttevagt d. ${DateUtility.parse(data.shift.dtStart).toFormat('dd-MM-yyyy')}` },
                React.createElement("p", null,
                    "Det er din egen vagt, du har udbudt til bytte. Den starter kl.",
                    " ",
                    React.createElement("strong", null, clock(data.shift.dtStart)),
                    " og slutter kl.",
                    " ",
                    React.createElement("strong", null, clock(data.shift.dtEnd)),
                    "."),
                React.createElement("p", null,
                    "Del linket med andre frivillige. N\u00E5r nogen svarer, kan du se og besvare deres bytteforslag p\u00E5 ",
                    React.createElement("a", { className: "bi-bv-inline-link", href: "/min-side/mine-byttevagter/" }, "Mine byttevagter"),
                    "."),
                React.createElement("div", { className: "bi-bv-pair" },
                    React.createElement(CopyLinkButton, { slug: data.slug, className: "bi-bv-btn bi-bv-btn-outline" }),
                    React.createElement("button", { type: "button", className: "bi-bv-btn bi-bv-btn-danger", onClick: () => this.setState({ confirmingWithdraw: true }) }, "Tr\u00E6k tilbage"))),
            this.state.confirmingWithdraw
                ? React.createElement(CancelExchangeConfirm, { shiftExchangeId: data.id, shiftMemberId: data.shiftMemberId, onCancelled: () => {
                        this.setState({ confirmingWithdraw: false });
                        window.dispatchEvent(new CustomEvent(SWAP_CHANGED_EVENT));
                        // The card now says the byttevagt is withdrawn, which is what the
                        // shared link will say from here on.
                        this.load();
                    }, onClose: () => this.setState({ confirmingWithdraw: false }), onError: (message) => this.setState({ confirmingWithdraw: false, error: message }) })
                : null));
    }
    render() {
        let data = this.state.data;
        if (this.state.swapped)
            return this.renderSwapped();
        if (!this.state.isLoaded) {
            return React.createElement(BvPage, { title: "Byttevagt" },
                React.createElement("div", { className: "bp-loading" },
                    React.createElement("i", { className: "fa fa-spinner fa-pulse fa-fw" })));
        }
        if (this.state.result) {
            return (React.createElement(BvPage, { title: "Byttevagt" },
                React.createElement(BvCard, { title: "Vagt overtaget" },
                    React.createElement("p", null,
                        "Du har overtaget en vagt d. ",
                        whenText(this.state.result.shiftStartTime),
                        " fra ",
                        this.state.result.substitutedMemberName),
                    React.createElement("a", { className: "bi-bv-link", href: this.state.result.returnUrl || BYTTEVAGTER_URL }, "Tilbage"))));
        }
        if (this.state.error) {
            return (React.createElement(BvPage, { title: "Byttevagt" },
                React.createElement(BvCard, { title: "Der skete en fejl" },
                    React.createElement("p", null, this.state.error),
                    React.createElement("a", { className: "bi-bv-link", href: BYTTEVAGTER_URL }, "Se flere byttevagter"))));
        }
        // The byttevagt is no longer on the exchange. Its row survives so the link shared into a
        // Facebook group still resolves, which is what gets us here.
        if (data.isOpen === false) {
            const withdrawn = data.status === BPShiftExchangeStatus.cancelled;
            return (React.createElement(BvPage, { title: "Byttevagt" },
                React.createElement(BvCard, { title: withdrawn ? 'Byttevagten er trukket tilbage' : 'Vagten er allerede overtaget' },
                    React.createElement("p", null, withdrawn ? 'Vagten er ikke længere på byttebørsen.' : 'En anden frivillig har taget denne vagt.'),
                    React.createElement("a", { className: "bi-bv-link", href: BYTTEVAGTER_URL }, "Se flere byttevagter"))));
        }
        // The viewer's own byttevagt. Offering to take over your own shift is nonsense, and a
        // volunteer arriving here from their own shared link should be sent to where they can
        // actually act on it.
        const isOwner = data.shiftMember && data.shiftMember.memberId === this.props.memberudi;
        if (isOwner)
            return this.renderOwner(data);
        // Both answered by the server for this volunteer, because both can only be answered about
        // one particular person: whether they could take the shift outright, and whether they have
        // anything a swap for it could be built from. They are separate questions - a shift somebody
        // cannot take is often exactly the one they can swap for, by offering the shift that
        // clashes - so each button follows its own.
        const canAcquire = data.canAcquire !== false;
        // Ghost slots are owned by Ubesat, so there is nobody on the other side to trade with.
        const canPropose = data.yellow === false && data.canSwap !== false;
        // Neither way in. Say so once, rather than leaving an empty card the volunteer has to
        // work out for themselves.
        const canDoNothing = !canAcquire && !canPropose;
        // "Byt med mig" is a screen of its own once it is open. A swap is an additional way to answer
        // a byttevagt, not a replacement for taking it outright, so it only opens from the card.
        if (canPropose && this.state.proposing) {
            return (React.createElement(BvPage, { title: "Byt med mig", className: "bi-bv-propose" },
                React.createElement(SwapOfferProposeComponent, { ref: this.proposeRef, slug: data.slug, ownername: data.shiftMember.name, header: this.renderProposeHeader(data), 
                    // It is "Fortryd" whether or not there is an offer to take back; the
                    // question that follows when there is says what it will do.
                    onCancel: this.cancelProposing, onOfferStanding: (standing) => this.setState({ offerStanding: standing }), onSwapped: (offer) => this.setState({ swapped: offer }) }),
                this.state.confirmingCancel
                    ? React.createElement(ConfirmDialog, { heading: "Tr\u00E6k bytteforslag tilbage", message: "Dette vil tr\u00E6kke dit byttetilbud tilbage. Er du sikker?", busy: this.state.cancelling, onYes: () => this.confirmCancel(), onNo: () => this.setState({ confirmingCancel: false }) })
                    : null));
        }
        return (React.createElement(BvPage, { title: "Byttevagt" },
            React.createElement("div", { className: "bi-bv-card-wrap" },
                React.createElement("div", { className: "bi-bv-card" },
                    React.createElement("h2", null,
                        "Overtag ",
                        data.shiftMember.role === 'Operator' ? 'operatørvagt' : 'kioskvagt',
                        " d. ",
                        DateUtility.parse(data.shift.dtStart).toFormat('dd-MM-yyyy')),
                    canDoNothing
                        ? React.createElement("div", { className: "bi-bv-alert bi-bv-alert-warn", role: "alert" },
                            React.createElement("i", { className: "fa-solid fa-triangle-exclamation", "aria-hidden": "true" }),
                            React.createElement("span", null, "Du kan ikke overtage denne vagt"))
                        : !canAcquire && canPropose
                            ? React.createElement("div", { className: "bi-bv-alert", role: "alert" },
                                React.createElement("span", null, "Du kan ikke overtage vagten direkte, men du kan tilbyde en af dine andre vagter til bytte"))
                            : null,
                    canAcquire
                        ? React.createElement("p", null,
                            "Du overtager vagten fra ",
                            React.createElement("span", { className: "bi-bv-from" },
                                roleIcon(data.shiftMember.role),
                                data.shiftMember.name),
                            ".")
                        : null,
                    this.renderShift(data),
                    React.createElement("div", { className: "bi-bv-actions" }, canDoNothing
                        // Nothing to answer with, so the only thing left is a way out. The
                        // notice at the top of the card has said why.
                        ? React.createElement("a", { className: "bi-bv-btn bi-bv-btn-outline", href: BYTTEVAGTER_URL }, "Ok")
                        : React.createElement(React.Fragment, null,
                            canAcquire
                                ? React.createElement("button", { className: "bi-bv-btn bi-bv-btn-success", onClick: () => this.setState({ confirmingTake: true }), disabled: this.state.submitDisabled }, "Overtag")
                                : null,
                            canPropose
                                // The only answer when taking the shift is out of the question,
                                // so it is the filled button then; beside "Overtag" it is the
                                // other way in.
                                ? React.createElement("button", { className: `bi-bv-btn ${canAcquire ? "bi-bv-btn-outline" : "bi-bv-btn-primary"}`, onClick: () => this.openSwap() }, "Byt med mig")
                                : null)))),
            this.state.confirmingTake
                ? React.createElement(ConfirmDialog, { heading: "Overtag vagt", message: overtagQuestion(whenPhrase(data.shift.dtStart), data.shiftMember.name), yesLabel: "Ja, overtag", positive: true, busy: this.state.submitDisabled, onYes: () => { this.setState({ confirmingTake: false }); this.submit(); }, onNo: () => this.setState({ confirmingTake: false }) })
                : null));
    }
}
ReactRenderer.render("shiftexchangeformcomponent", (elem) => React.createElement(ShiftExchangeFormComponent, Object.assign({}, (elem.dataset))));
